/* Copyright (c) 2024 MOCA Systems, Inc.	All rights reserved. */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Observable, Subject, combineLatest, map, startWith, take, takeUntil, withLatestFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/common/material/material-module';
import * as appStrings from '@touchplan/strings';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import { ImportTicketsStateService } from '../../services/import-tickets-state.service';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';
import { CheckboxListModelService } from 'src/app/shared/ui/material/checkbox-list/checkbox-list-model.service';
import { IESIDData, Ticket } from '@touchplan/common';
import { ImportTicketsCommonService } from '../../services/import-tickets-common.service';
import { TicketThumbnailComponent } from 'src/app/components/ticket/ticket-thumbnail/ticket-thumbnail.component';
import {
	EsidChecklistDataMap,
	EsidDisambiguationService,
	IEsidChecklistItemData,
} from '../../services/esid-disambiguation.service';

@Component({
	selector: 'esid-disambiguation',
	standalone: true,
	imports: [
		CommonModule,
		MaterialModule,
		MatDialogModule,
		PresentationModule,
		ReactiveFormsModule,
		SharedUIMaterialModule,
		TicketThumbnailComponent,
	],
	providers: [EsidDisambiguationService],
	templateUrl: './esid-disambiguation.component.html',
	styleUrls: ['./esid-disambiguation.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsidDisambiguationComponent implements OnInit, OnDestroy {
	private importTicketsState = inject(ImportTicketsStateService);
	private importTicketsCommonService = inject(ImportTicketsCommonService);
	private fb = inject(FormBuilder);
	private esidDisambiguationService = inject(EsidDisambiguationService);

	protected appStrings = appStrings;

	private destroyed$: Subject<void> = new Subject();

	protected formGroup: FormGroup<{
		esidDisambiguationCheckList: FormControl<EsidChecklistDataMap>;
	}> = this.fb.group({
		esidDisambiguationCheckList: [],
	});

	private esidDisambiguationDataMap$: Observable<EsidChecklistDataMap> = combineLatest([
		// The take(1) is here on purpose to avoid doing a 're-sort' of the list and we apply it knowing that
		// the esidDisambiguation$ property of the importTicketsState is set before opening this step and we don't need to listen for events
		this.importTicketsState.esidData$.pipe(take(1)),
		this.importTicketsState.ticketList,
		this.importTicketsState.swimlanesList,
	]).pipe(
		withLatestFrom(
			this.formGroup.controls['esidDisambiguationCheckList'].valueChanges.pipe(startWith(new Map()))
		),
		map(([[esidDisambiguation, ticketList, swimlanesList], esidDisambiguationDataMap]) =>
			this.esidDisambiguationService.getEsidDisambiguationCheckList(
				esidDisambiguation,
				ticketList,
				swimlanesList,
				esidDisambiguationDataMap
			)
		)
	);

	protected esidDisambiguationCheckList$: Observable<IEsidChecklistItemData[]> =
		this.esidDisambiguationDataMap$.pipe(
			map(() => {
				return CheckboxListModelService.convertMapToArray(
					this.formGroup.controls['esidDisambiguationCheckList'].value
				);
			})
		);

	protected createNewTicketsIds$: Observable<string[]> = this.esidDisambiguationCheckList$.pipe(
		map((esidDisambiguationCheckList) =>
			esidDisambiguationCheckList
				.filter(({ originalModel }) => originalModel?.isCreateCheckBox)
				.map(({ id }) => id)
		)
	);

	protected updateTicketsIds$: Observable<string[]> = this.esidDisambiguationCheckList$.pipe(
		map((esidDisambiguationCheckList) =>
			esidDisambiguationCheckList
				.filter(({ originalModel }) => !originalModel?.isCreateCheckBox)
				.map(({ id }) => id)
		)
	);

	protected saveOrCloseText$: Observable<string> = this.importTicketsState.needsVariance$.pipe(
		map((needsVariance) =>
			needsVariance?.length
				? appStrings.UICONTROL_TEXT_NEXT
				: appStrings.IMPORT_TICKET_MAP_VARIANCE_REASONS_STEP_NEXT_LABEL
		)
	);

	protected focusTicket(ticket: Ticket): void {
		if (!ticket) {
			return;
		}

		this.importTicketsCommonService.focusTicket$.next(ticket);
	}

	ngOnInit(): void {
		this.esidDisambiguationDataMap$
			.pipe(take(1), takeUntil(this.destroyed$))
			.subscribe((esidDisambiguationDataMap) => {
				this.formGroup.controls['esidDisambiguationCheckList'].setValue(esidDisambiguationDataMap);
			});
	}

	public onNext() {
		const esidDisambiguationCheckList = this.formGroup.value.esidDisambiguationCheckList;
		const esidDisambiguationSelection: IESIDData =
			this.esidDisambiguationService.getEsidDisambiguation(esidDisambiguationCheckList);
		this.importTicketsState.nextStep(esidDisambiguationSelection);
	}

	ngOnDestroy(): void {
		this.destroyed$.next();
		this.destroyed$.complete();
	}
}
